import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  AskAISignalApiParams,
  AskAiSignalDeletePayload,
  AskAISignalFieldsResponse,
  AskAISignalItemType,
  AskAISignalModelOptionType,
  GetAskAISignalFieldsParams,
  PreviewAskAiEnrichedDataParams,
  RefinePromptParams,
  RefinePromptResponse,
  RefineQueryPayloadType,
  RefineQueryResponseType,
  SaveAskAiConfigParams,
  SignalSaveQueryPayloadType,
  UpdateAskAiConfigParams,
  UpdateSignalQueryPayloadType,
} from './types';
import axios from 'axios';
import { apiClient } from 'src/api/apiClient';
import {
  ADD_NEW_CUSTOM_AI_SIGNAL,
  GET_ASK_AI_FIELDS,
  GET_ASK_AI_MODELS,
  GET_ASK_AI_SIGNAL_DATA,
  GET_ASK_AI_SUGGESTED_COLUMN_NAME,
  REFINE_ASK_AI_PROMPT,
  REFINE_QUERY,
  SAVE_ASK_AI_CONFIG,
  UPDATE_CUSTOM_AI_SIGNAL_NEW,
  GET_AI_NEWS_SMART_SEARCH,
  DELETE_ASK_AI_CONFIG,
  ASK_AI_FORCE_TERMINATE,
  UPDATE_ASK_AI_CONFIG,
  GET_GEO_LOCATIONS,
  RUN_CELL_CUSTOM_AI_SIGNAL_NEW,
  RUN_COLUMN_CUSTOM_AI_SIGNAL_NEW,
  RUN_ASK_AI_CELL,
  PREVIEW_ASK_AI_DATA,
  GET_SIGNAL_LIBRARY_COUNT,
  SIGNAL_TERMINATE,
} from 'src/constants/httpEndPoints';

const ML_SERVICE_AUTH = 'cust0m@i2024';
const SIGNAL_LIBRARY_API_KEY = 'Ce9k7NGgy1TNMKGN';

// Create a separate axios instance without interceptors for custom authorization
const axiosWithoutInterceptors = axios.create();

interface DeleteAskAiConfigurationsPayload {
  AI_SIGNAL: number[];
  ASK_AI: number[];
  emailId: string;
  customerId: string;
}

const deleteAskAiConfigurations = async ({
  AI_SIGNAL,
  ASK_AI,
  emailId,
  customerId,
}: DeleteAskAiConfigurationsPayload): Promise<{ deleted: number[]; failed: string[] }> => {
  const response = await apiClient.post(
    DELETE_ASK_AI_CONFIG,
    { AI_SIGNAL, ASK_AI },
    {
      headers: {
        'x-customer-id': customerId,
        'x-login-email': emailId,
      },
    },
  );
  return {
    deleted: (response.data?.deleted ?? []).map(Number),
    failed: response.data?.failed ?? [],
  };
};

export const getAskAiSignalModels = createAsyncThunk(
  'ask-ai-signal/getAiModels',
  async (params: AskAISignalApiParams): Promise<AskAISignalModelOptionType[]> => {
    const { customerId, emailId } = params;

    const response = await apiClient.post(
      GET_ASK_AI_MODELS,
      {},
      {
        headers: {
          'x-customer-id': customerId,
          'x-login-email': emailId,
        },
      },
    );
    return response.data || [];
  },
);

export const getAskAISignalFields = createAsyncThunk(
  'ask-ai-signal/getAskAiSignalFields',
  async (params: GetAskAISignalFieldsParams): Promise<AskAISignalFieldsResponse> => {
    const { type, customerId, emailId } = params;

    const response = await apiClient.post(
      GET_ASK_AI_FIELDS,
      { type },
      {
        headers: {
          'x-customer-id': customerId,
          'x-login-email': emailId,
        },
      },
    );
    return response.data as AskAISignalFieldsResponse;
  },
);

export const getAskAiSignalData = createAsyncThunk(
  'ask-ai-signal/getAskAiSignalData',
  async (payload: AskAISignalApiParams): Promise<AskAISignalItemType[]> => {
    const { customerId, emailId } = payload;

    const response = await apiClient.post(
      GET_ASK_AI_SIGNAL_DATA,
      {},
      {
        headers: {
          'x-customer-id': customerId,
          'x-login-email': emailId,
        },
      },
    );
    return response.data || [];
  },
);

export const refinePrompt = createAsyncThunk<RefinePromptResponse, RefinePromptParams>(
  'ask-ai/refinePrompt',
  async ({ prompt, selectedColumns, allColumns, researchModel, useSprouter }: RefinePromptParams) => {
    const response = await axiosWithoutInterceptors.post(
      REFINE_ASK_AI_PROMPT,
      { prompt, selectedColumns, allColumns, researchModel, useSprouter },
      {
        headers: {
          Authorization: ML_SERVICE_AUTH,
        },
      },
    );
    return {
      refinedQuery: response.data?.refine_prompt,
      suggestedResponseType: response.data?.suggestedResponseType,
    }
  },
);

export const getAskAiSuggestedColumnName = createAsyncThunk<
  Record<string, any>,
  { userQuery: string; allColumns: Record<string, number> }
>('ask-ai-signal/getAskAiSuggestedColumnName', async (payload) => {
  const { userQuery, allColumns } = payload;
  const response = await axiosWithoutInterceptors.post(
    GET_ASK_AI_SUGGESTED_COLUMN_NAME,
    {
      userQuery: userQuery,
      allColumns: allColumns,
    },
    {
      headers: {
        Authorization: ML_SERVICE_AUTH,
      },
    },
  );
  return (response.data);
});

export const getSignalRefineQueryStatus = createAsyncThunk<
  RefineQueryResponseType,
  RefineQueryPayloadType
>('ask-ai-signal/getSignalRefineQueryStatus', async (payload) => {
  const res = await apiClient.post(REFINE_QUERY, {
    query: payload.query,
    country_name: payload.country_name || 'Global',
  });
  return res.data as RefineQueryResponseType;
});

export const submitSignalSaveQuery = createAsyncThunk<any, SignalSaveQueryPayloadType>(
  'ask-ai-signal/submitSignalSaveQuery',
  async (payload, { rejectWithValue }) => {
    const {
      displayName,
      filterDataDTO,
      isCustomSignal,
      isEnableForFutureAccounts,
      isEnrichAllAccounts,
      keyword,
      numberOfAccounts,
      order,
      query,
      selectedAccounts,
      sortColumn,
      email,
      customerId,
      preconfigSignalPk,
      userQuery,
      queryRefinementResponse,
      dateRange,
      geoLocation,
      refreshFrequency,
      responseType,
      aiModel,
      userCustomerHeaderDetailPks,
      customFormat,
    } = payload;
    const body = {
      displayName: displayName,
      keyword: keyword,
      query: query,
      isEnableForFutureAccounts: isEnableForFutureAccounts,
      isEnrichAllAccounts: isEnrichAllAccounts,
      selectedAccounts: selectedAccounts,
      numberOfAccounts: numberOfAccounts,
      filterDataDTO: filterDataDTO,
      order: order,
      sortColumn: sortColumn,
      isCustomSignal: isCustomSignal,
      preconfigSignalPk,
      userQuery,
      queryRefinementResponse,
      dateRange,
      geoLocation,
      responseType,
      ...(refreshFrequency && { refreshFrequency }),
      aiModel,
      userCustomerHeaderDetailPks,
      ...(customFormat && { customFormat }),
    };
    try {
      const res = await apiClient.post(ADD_NEW_CUSTOM_AI_SIGNAL, body, {
        headers: {
          'X-Login-Email': email,
          'X-Customer-Id': customerId,
        },
      });
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.error ||
          'Column name already exists. Please insert another column name.',
      );
    }
  },
);

export const updateSignalQuery = createAsyncThunk<any, UpdateSignalQueryPayloadType, { rejectValue: string }>(
  'ask-ai-signal/update-signal-query',
  async (payload, { rejectWithValue }) => {
    const {
      id,
      filterDataDTO,
      isEnableForFutureAccounts,
      isEnrichAllAccounts,
      numberOfAccounts,
      order,
      selectedAccounts,
      sortColumn,
      email,
      customerId,
      dateRange,
      geoLocation,
      responseType,
      aiModel,
      refreshFrequency,
      displayName,
      customFormat,
    } = payload;
    const body = {
      id: id,
      isEnableForFutureAccounts: isEnableForFutureAccounts,
      isEnrichAllAccounts: isEnrichAllAccounts,
      selectedAccounts: selectedAccounts || [],
      numberOfAccounts: numberOfAccounts || 0,
      filterDataDTO: filterDataDTO || [],
      order: order || '',
      sortColumn: sortColumn || '',
      ...(refreshFrequency && { refreshFrequency }),
      ...(displayName && { displayName }),
      dateRange: dateRange || 'Past 6 Months',
      ...(geoLocation && { geoLocation }),
      ...(responseType && { responseType }),
      ...(aiModel && { aiModel }),
      ...(customFormat && { customFormat }),
    };
    try {
      const res = await apiClient.post(UPDATE_CUSTOM_AI_SIGNAL_NEW, body, {
        headers: {
          'X-Login-Email': email,
          'X-Customer-Id': customerId,
        },
      });
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.error || 'Failed to update signal',
      );
    }
  },
);

export const getResearchTemplateCategories = createAsyncThunk<any, { input?: string }>(
  'ask-ai-signal/getResearchTemplateCategories',
  async (payload) => {
    const { input = '' } = payload;
    const res = await apiClient.get(`${GET_AI_NEWS_SMART_SEARCH}?input=${input || ''}`, {
      headers: {
        'X-API-Key': SIGNAL_LIBRARY_API_KEY,
      },
    });
    return res.data.data;
  },
);

export const submitAskAiSaveConfig = createAsyncThunk<
  AskAISignalItemType,
  SaveAskAiConfigParams,
  { rejectValue: string }
>('ask-ai-signal/submitAskAiSaveConfig', async (params, { rejectWithValue }) => {
  const {
    customerId,
    emailId,
    displayName,
    type,
    query,
    aiModel,
    webSearchEnabled,
    useContactSocial,
    userCustomerHeaderDetailPks = [],
    numberOfAccounts = 0,
    selectedAccounts = [],
    isEnrichAllAccounts = false,
    numberOfContacts = 0,
    selectedContacts = [],
    isEnrichAllContacts = false,
    isEnableForFutureAccounts,
    isEnableForFutureContacts,
    filterDataDTO = [],
    order = '',
    sortColumn = '',
    dateRange,
    geoLocation,
    responseType,
    customFormat,
  } = params;

  const payload: Record<string, unknown> = {
    displayName,
    type,
    query,
    aiModel,
    webSearchEnabled,
    ...(useContactSocial && { useContactSocial }),
    userCustomerHeaderDetailPks,
    filterDataDTO,
    order,
    sortColumn,
    dateRange,
    geoLocation,
    responseType,
    ...(customFormat && { customFormat }),
  };

  // Add account-specific fields if type is ACCOUNT
  if (type === 'ACCOUNT') {
    payload.numberOfAccounts = numberOfAccounts;
    payload.selectedAccounts = selectedAccounts;
    payload.isEnrichAllAccounts = isEnrichAllAccounts;
    if (isEnableForFutureAccounts !== undefined) {
      payload.isEnableForFutureAccounts = isEnableForFutureAccounts;
    }
  } else {
    // Add contact-specific fields if type is CONTACT
    payload.numberOfContacts = numberOfContacts;
    payload.selectedContacts = selectedContacts;
    payload.isEnrichAllContacts = isEnrichAllContacts;
    if (isEnableForFutureContacts !== undefined) {
      payload.isEnableForFutureContacts = isEnableForFutureContacts;
    }
  }

  try {
    const response = await apiClient.post(SAVE_ASK_AI_CONFIG, payload, {
      headers: {
        'x-customer-id': customerId,
        'x-login-email': emailId,
      },
    });

    const responseData = response.data as AskAISignalItemType;

    // Map response to AskAiDataItem format
    const savedConfig: AskAISignalItemType = {
      primaryKey: responseData.primaryKey,
      displayName: responseData.displayName,
      type: responseData.type,
      query: responseData.query,
      aiModel: responseData.aiModel,
      webSearchEnabled: responseData.webSearchEnabled,
      useContactSocial: responseData.useContactSocial,
      userCustomerHeaderDetailPks: responseData.userCustomerHeaderDetailPks
        ? responseData.userCustomerHeaderDetailPks.map(Number)
        : [],
      total: 0,
      enriched: 0,
      pending: 0,
      columnName: responseData.columnName,
      lastModifiedDate: responseData.lastModifiedDate,
      credit: responseData.credit,
      responseType: responseData.responseType,
      featureName: responseData.featureName,
      dateRange: responseData.dateRange,
      geoLocation: responseData.geoLocation,
    };

    return savedConfig;
  } catch (error: unknown) {
    const errorMessage =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
    return rejectWithValue(errorMessage || 'Failed to save Ask AI config');
  }
});

export const updateAskAiConfig = createAsyncThunk<
  any,
  UpdateAskAiConfigParams,
  { rejectValue: string }
>('ask-ai-signal/updateAskAiConfig', async (params, { rejectWithValue }) => {
  const {
    customerId,
    emailId,
    primaryKey,
    aiModel,
    webSearchEnabled,
    useContactSocial,
    dateRange,
    geoLocation,
    numberOfAccounts = 0,
    selectedAccounts = [],
    isEnrichAllAccounts = false,
    isEnableForFutureAccounts,
    numberOfContacts = 0,
    selectedContacts = [],
    isEnrichAllContacts = false,
    isEnableForFutureContacts,
    filterDataDTO = [],
    order = '',
    sortColumn = '',
    type,
    displayName,
    responseType,
    customFormat,
  } = params;

  const payload: Record<string, unknown> = {
    primaryKey,
    type,
    aiModel,
    webSearchEnabled,
    filterDataDTO,
    order,
    sortColumn,
  };

  if(displayName) {
    payload.displayName = displayName;
  }

  if (responseType) {
    payload.responseType = responseType;
  }

  if (dateRange) {
    payload.dateRange = dateRange;
  }

  if (geoLocation) {
    payload.geoLocation = geoLocation;
  }

  if (customFormat) {
    payload.customFormat = customFormat;
  }

  if (useContactSocial) {
    payload.useContactSocial = useContactSocial;
  }

  // Add account-specific fields if type is ACCOUNT
  if (type === 'ACCOUNT') {
    payload.numberOfAccounts = numberOfAccounts;
    payload.selectedAccounts = selectedAccounts;
    payload.isEnrichAllAccounts = isEnrichAllAccounts;
    if (isEnableForFutureAccounts !== undefined) {
      payload.isEnableForFutureAccounts = isEnableForFutureAccounts;
    }
  } else {
    // Add contact-specific fields if type is CONTACT
    payload.numberOfContacts = numberOfContacts;
    payload.selectedContacts = selectedContacts;
    payload.isEnrichAllContacts = isEnrichAllContacts;
    if (isEnableForFutureContacts !== undefined) {
      payload.isEnableForFutureContacts = isEnableForFutureContacts;
    }
  }

  try {
    const response = await apiClient.post(UPDATE_ASK_AI_CONFIG, payload, {
      headers: {
        'x-customer-id': customerId,
        'x-login-email': emailId,
      },
    });

    return response;
  } catch (error: unknown) {
    const errorMessage =
      error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
    return rejectWithValue(errorMessage || 'Failed to save Ask AI config');
  }
});

export const deleteAskAiSignalConfig = createAsyncThunk<
  number,
  AskAiSignalDeletePayload,
  { rejectValue: string }
>(
  'ask-ai-signal/deleteAskAiSignalConfig',
  async (payload, { rejectWithValue }) => {
    const { id, emailId, customerId } = payload;
    const { deleted } = await deleteAskAiConfigurations({
      AI_SIGNAL: [id],
      ASK_AI: [],
      emailId,
      customerId,
    });
    if (!deleted.includes(id)) {
      return rejectWithValue(
        'Failed to delete the selected signal. It may have already been removed.',
      );
    }
    return id;
  },
);

export const deleteAskAiConfig = createAsyncThunk<
  { id: number },
  AskAiSignalDeletePayload,
  { rejectValue: string }
>(
  'ask-ai-signal/deleteAskAiConfig',
  async (params, { rejectWithValue }) => {
    const { id, customerId, emailId } = params;
    const { deleted } = await deleteAskAiConfigurations({
      AI_SIGNAL: [],
      ASK_AI: [id],
      emailId,
      customerId,
    });
    if (!deleted.includes(id)) {
      return rejectWithValue(
        'Failed to delete the selected Ask AI signal. It may have already been removed.',
      );
    }
    return { id };
  },
);

export const getGeoLocations = createAsyncThunk<any, any>(
  'ask-ai-signal/get-geo-location',
  async (payload) => {
    const { emailId, customerId, searchText } = payload;
    const res = await apiClient.post(
      `${GET_GEO_LOCATIONS}${searchText ? `&searchText=${searchText}` : ''}`,
      {},
      {
        headers: {
          'X-Login-Email': emailId,
          'X-Customer-Id': customerId,
        },
      },
    );
    return res;
  },
);

export const terminateSignal = createAsyncThunk<any, { emailId: string; customerId: string }>(
  'ask-ai-signal/terminate',
  async ({ emailId, customerId }, { rejectWithValue }) => {
    const headers = {
      'X-Login-Email': emailId,
      'X-Customer-Id': customerId,
    };

    try {
      const [forceTerminateResult, legacyTerminateResult] = await Promise.allSettled([
        apiClient.post(ASK_AI_FORCE_TERMINATE, {}, { headers }),
        apiClient.post(SIGNAL_TERMINATE, {}, { headers }),
      ]);

      if (
        forceTerminateResult.status === 'rejected' &&
        legacyTerminateResult.status === 'rejected'
      ) {
        throw forceTerminateResult.reason || legacyTerminateResult.reason;
      }

      if (forceTerminateResult.status === 'fulfilled') {
        return forceTerminateResult.value.data;
      }

      if (legacyTerminateResult.status === 'fulfilled') {
        return legacyTerminateResult.value.data;
      }

      return null;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data?.message || 'Failed to terminate signal');
    }
  },
);

export const runSignalCell = createAsyncThunk<any, UpdateSignalQueryPayloadType>(
  'ask-ai-signal/run-signal-cell',
  async (payload) => {
    const {
      id,
      filterDataDTO,
      isEnableForFutureAccounts,
      isEnrichAllAccounts,
      numberOfAccounts,
      order,
      selectedAccounts,
      sortColumn,
      email,
      customerId,
      dateRange,
      refreshFrequency,
      displayName,
      geoLocation,
      responseType,
      aiModel,
      customFormat,
    } = payload;
    const body = {
      id: id,
      isEnableForFutureAccounts: isEnableForFutureAccounts,
      isEnrichAllAccounts: isEnrichAllAccounts,
      selectedAccounts: selectedAccounts,
      numberOfAccounts: numberOfAccounts,
      filterDataDTO: filterDataDTO,
      order: order,
      sortColumn: sortColumn,
      dateRange,
      ...(refreshFrequency && { refreshFrequency }),
      ...(displayName && { displayName }),
      ...(geoLocation && { geoLocation }),
      ...(responseType && { responseType }),
      ...(aiModel && { aiModel }),
      ...(customFormat && { customFormat }),
    };
    const res = await apiClient.post(RUN_CELL_CUSTOM_AI_SIGNAL_NEW, body, {
      headers: {
        'X-Login-Email': email,
        'X-Customer-Id': customerId,
      },
    });
    return res.data;
  },
);

export const runSignalColumn = createAsyncThunk<any, UpdateSignalQueryPayloadType>(
  'ask-ai-signal/run-signal-column',
  async (payload) => {
    const {
      id,
      filterDataDTO,
      isEnableForFutureAccounts,
      isEnrichAllAccounts,
      numberOfAccounts,
      order,
      selectedAccounts,
      sortColumn,
      email,
      customerId,
      dateRange,
      refreshFrequency,
      displayName,
      geoLocation,
      responseType,
      aiModel,
      customFormat,
    } = payload;
    const body = {
      id: id,
      isEnableForFutureAccounts: isEnableForFutureAccounts,
      isEnrichAllAccounts: isEnrichAllAccounts,
      selectedAccounts: selectedAccounts,
      numberOfAccounts: numberOfAccounts,
      filterDataDTO: filterDataDTO,
      order: order,
      sortColumn: sortColumn,
      dateRange,
      ...(refreshFrequency && { refreshFrequency }),
      ...(displayName && { displayName }),
      ...(geoLocation && { geoLocation }),
      ...(responseType && { responseType }),
      ...(aiModel && { aiModel }),
      ...(customFormat && { customFormat }),
    };
    const res = await apiClient.post(RUN_COLUMN_CUSTOM_AI_SIGNAL_NEW, body, {
      headers: {
        'X-Login-Email': email,
        'X-Customer-Id': customerId,
      },
    });
    return res.data;
  },
);

export const runAskAiColumn = createAsyncThunk<any, UpdateAskAiConfigParams>(
  'ask-ai-signal/runAskAiColumn',
  async (payload) => {
    const {
      primaryKey,
      type,
      numberOfAccounts,
      selectedAccounts,
      isEnrichAllAccounts,
      numberOfContacts,
      selectedContacts,
      isEnrichAllContacts,
      filterDataDTO,
      order,
      sortColumn,
      emailId,
      customerId,
    } = payload;

    const body = {
      primaryKey,
      ...(type === 'ACCOUNT' && {
        numberOfAccounts: Number(numberOfAccounts),
        selectedAccounts,
        isEnrichAllAccounts,
      }),
      ...(type === 'CONTACT' && {
        numberOfContacts: Number(numberOfContacts),
        selectedContacts,
        isEnrichAllContacts,
      }),
      filterDataDTO,
      order,
      sortColumn,
    };

    const res = await apiClient.post(RUN_ASK_AI_CELL, body, {
      headers: {
        'X-Login-Email': emailId,
        'X-Customer-Id': customerId,
      },
    });

    return res.data;
  },
);

export const runAskAiCell = createAsyncThunk<any, UpdateAskAiConfigParams>(
  'ask-ai-signal/runAskAiCell',
  async (payload) => {
    const {
      primaryKey,
      type,
      numberOfAccounts,
      selectedAccounts,
      isEnrichAllAccounts,
      numberOfContacts,
      selectedContacts,
      isEnrichAllContacts,
      filterDataDTO,
      order,
      sortColumn,
      emailId,
      customerId,
    } = payload;

    const body = {
      primaryKey,
      ...(type === 'ACCOUNT' && {
        numberOfAccounts,
        selectedAccounts,
        isEnrichAllAccounts,
      }),
      ...(type === 'CONTACT' && {
        numberOfContacts,
        selectedContacts,
        isEnrichAllContacts,
      }),
      filterDataDTO,
      order,
      sortColumn,
    };

    const res = await apiClient.post(RUN_ASK_AI_CELL, body, {
      headers: {
        'X-Login-Email': emailId,
        'X-Customer-Id': customerId,
      },
    });

    return res.data;
  },
);

export const previewAskAiData = createAsyncThunk<
  { data: Array<{ name: string; customPromptResponse: string }> },
  PreviewAskAiEnrichedDataParams
>('ask-ai/previewAskAiData', async (payload: PreviewAskAiEnrichedDataParams) => {
  const { userQuery, refinedQuery, entityType, modelUsed, webSearch, accountsData, contactsData, country_name, temporal_recency, responseType } =
    payload;
  const body = {
    userQuery,
    refinedQuery,
    entityType,
    modelUsed,
    webSearch,
    accountsData,
    contactsData,
    country_name,
    temporal_recency,
    responseType,
  };
  const response = await axiosWithoutInterceptors.post(PREVIEW_ASK_AI_DATA, body, {
    headers: {
      Authorization: ML_SERVICE_AUTH,
    },
  });
  return response.data;
});

export const bulkDeleteAskAiConfig = createAsyncThunk<
  { deleted: number[]; failedCount: number },
  { AI_SIGNAL: number[]; ASK_AI: number[]; emailId: string; customerId: string },
  { rejectValue: string }
>('ask-ai-signal/bulkDeleteAskAiConfig', async ({ AI_SIGNAL, ASK_AI, emailId, customerId }, { rejectWithValue }) => {
  const { deleted, failed } = await deleteAskAiConfigurations({
    AI_SIGNAL,
    ASK_AI,
    emailId,
    customerId,
  });
  if (deleted.length === 0 && failed.length > 0) {
    return rejectWithValue('Failed to delete the selected signals. They may have already been removed.');
  }
  return { deleted, failedCount: failed.length };
});

export const getTotalLibraryCount = createAsyncThunk<any, any>(
  'ask-ai-signal/get-library-count',
  async (payload) => {
    const { emailId, customerId } = payload;
    const res = await apiClient.get(
      `${GET_SIGNAL_LIBRARY_COUNT}`,
      {
        headers: {
          'X-Login-Email': emailId,
          'X-Customer-Id': customerId,
        },
      },
    );
    return res.data;
  },
);
